import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { FileText, Building, Filter, X, Truck, CheckSquare, Send } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { hasPermission, isAdmin } from '@/utils/permissions';
import { getCurrentUser } from '../utils/sessionManager';
import { SearchableItemSelect } from '@/components/ui/searchable-item-select';
import ActivityLogButton from './ActivityLogButton';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProcurementView = () => {
  const [pos, setPos] = useState([]);
  const [filteredPos, setFilteredPos] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPOs, setSelectedPOs] = useState(new Set());
  const [quantities, setQuantities] = useState({});
  const [userVendorId, setUserVendorId] = useState(null);
  
  // Date filter state
  const [dateFilter, setDateFilter] = useState({
    start_date: '',
    end_date: ''
  });
  
  const [filters, setFilters] = useState({
    po_number: '',
    vendor_id: '',
    item_id: '',
    order_date: '',
    need_date: '',
    status: '',
    sob_tag: '',
    qty_min: '',
    qty_max: ''
  });

  // Get logged-in user's vendor ID and initialize once
  useEffect(() => {
    const initializeUser = async () => {
      try {
        const user = getCurrentUser();
        if (user) {
          if (user.vendor_id) {
            setUserVendorId(user.vendor_id);
          } else {
            // User doesn't have vendor_id, safe to fetch all data
            fetchData();
          }
        } else {
          // No user data, fetch all
          fetchData();
        }
      } catch (error) {
        console.error('Error getting user vendor ID:', error);
        // On error, fetch all data
        fetchData();
      }
    };
    
    initializeUser();
  }, []);

  useEffect(() => {
    // Only fetch when userVendorId is set (not null)
    if (userVendorId) {
      fetchData();
    }
  }, [userVendorId]);

  useEffect(() => {
    applyFilters();
  }, [pos, filters]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Build URL with date filters
      let posUrl = `${API}/procurement/pos`;
      const params = [];
      
      if (userVendorId) {
        params.push(`vendor_id=${userVendorId}`);
      }
      
      if (dateFilter.start_date) {
        params.push(`start_date=${dateFilter.start_date}`);
      }
      
      if (dateFilter.end_date) {
        params.push(`end_date=${dateFilter.end_date}`);
      }
      
      if (params.length > 0) {
        posUrl += '?' + params.join('&');
      }
      
      const [poRes, vendRes, itemRes] = await Promise.all([
        axios.get(posUrl),
        axios.get(`${API}/vendors`),
        axios.get(`${API}/items`)
      ]);
      
      // Handle response (array directly, no pagination wrapper)
      const poData = Array.isArray(poRes.data) ? poRes.data : (poRes.data.data || []);
      setPos(poData);
      
      setVendors(vendRes.data);
      setItems(itemRes.data);
      
      // Initialize quantities
      const qtys = {};
      poData.forEach(po => {
        qtys[po.id] = po.qty;
      });
      setQuantities(qtys);
    } catch (error) {
      console.error('Error fetching procurement data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Date filter handler
  const handleDateFilterChange = (field, value) => {
    setDateFilter(prev => ({ ...prev, [field]: value }));
  };

  const applyDateFilter = () => {
    fetchData();
  };

  const clearDateFilter = () => {
    setDateFilter({ start_date: '', end_date: '' });
    // Fetch after state update
    setTimeout(() => fetchData(), 0);
  };

  const isPastDate = (orderDate) => {
    if (!orderDate) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const poDate = new Date(orderDate);
    poDate.setHours(0, 0, 0, 0);
    return poDate < today;
  };

  const canSelectPO = (po) => {
    // Can only select DRAFT POs with non-past order dates
    return po.status === 'DRAFT' && !isPastDate(po.order_date);
  };

  const handleSelectPO = (poId) => {
    const po = pos.find(p => p.id === poId);
    if (!po || !canSelectPO(po)) {
      if (po && isPastDate(po.order_date)) {
        toast.error('Cannot select POs with past order dates');
      }
      return;
    }
    
    const newSelected = new Set(selectedPOs);
    if (newSelected.has(poId)) {
      newSelected.delete(poId);
    } else {
      newSelected.add(poId);
    }
    setSelectedPOs(newSelected);
  };

  const handleSelectAll = () => {
    const selectablePOs = filteredPos.filter(canSelectPO);
    
    if (selectedPOs.size === selectablePOs.length && selectablePOs.length > 0) {
      setSelectedPOs(new Set());
    } else {
      setSelectedPOs(new Set(selectablePOs.map(po => po.id)));
    }
  };

  const handleQuantityChange = (poId, newQty) => {
    setQuantities({
      ...quantities,
      [poId]: parseFloat(newQty) || 0
    });
  };

  const handleReleasePOs = async () => {
    if (selectedPOs.size === 0) {
      toast.error('Please select at least one PO to release');
      return;
    }

    const selectedIds = Array.from(selectedPOs);
    
    // Prepare quantities for POs that were modified
    const qtysToUpdate = {};
    selectedIds.forEach(id => {
      const po = pos.find(p => p.id === id);
      if (po && quantities[id] !== po.qty) {
        qtysToUpdate[id] = quantities[id];
      }
    });

    try {
      const response = await axios.post(`${API}/procurement/release-pos`, {
        po_ids: selectedIds,
        quantities: Object.keys(qtysToUpdate).length > 0 ? qtysToUpdate : null
      });

      toast.success(response.data.message, {
        description: `${response.data.successful} PO(s) released successfully`
      });

      if (response.data.errors.length > 0) {
        toast.warning(`${response.data.errors.length} PO(s) had errors`, {
          duration: 5000
        });
      }

      setSelectedPOs(new Set());
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to release POs');
    }
  };

  const applyFilters = () => {
    let filtered = [...pos];

    if (filters.po_number) {
      filtered = filtered.filter(p => p.po_number.toLowerCase().includes(filters.po_number.toLowerCase()));
    }

    if (filters.vendor_id && filters.vendor_id !== 'all') {
      filtered = filtered.filter(p => p.vendor_id === filters.vendor_id);
    }

    if (filters.item_id && filters.item_id !== 'all') {
      filtered = filtered.filter(p => p.item_id === filters.item_id);
    }

    if (filters.order_date) {
      filtered = filtered.filter(p => p.order_date.includes(filters.order_date));
    }

    if (filters.need_date) {
      filtered = filtered.filter(p => p.need_date.includes(filters.need_date));
    }

    if (filters.status && filters.status !== 'all') {
      filtered = filtered.filter(p => p.status === filters.status);
    }

    if (filters.sob_tag) {
      filtered = filtered.filter(p => p.sob_tag.includes(filters.sob_tag));
    }

    if (filters.qty_min) {
      filtered = filtered.filter(p => p.qty >= parseFloat(filters.qty_min));
    }

    if (filters.qty_max) {
      filtered = filtered.filter(p => p.qty <= parseFloat(filters.qty_max));
    }

    setFilteredPos(filtered);
  };

  const clearFilters = () => {
    setFilters({
      po_number: '',
      vendor_id: '',
      item_id: '',
      order_date: '',
      need_date: '',
      status: '',
      sob_tag: '',
      qty_min: '',
      qty_max: ''
    });
  };

  const getVendorName = (id) => vendors.find(v => v.id === id)?.name || id;
  const getVendorInfo = (id) => vendors.find(v => v.id === id) || {};
  const getItemInfo = (id) => {
    const item = items.find(i => i.id === id);
    return item ? `${item.part_number} - ${item.description}` : id;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'DRAFT': return 'bg-yellow-100 text-yellow-800';
      case 'SENT': return 'bg-blue-100 text-blue-800';
      case 'CONFIRMED': return 'bg-green-100 text-green-800';
      case 'RECEIVED': return 'bg-purple-100 text-purple-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const hasActiveFilters = Object.values(filters).some(v => v !== '');
  const uniqueStatuses = ['DRAFT', 'SENT', 'CONFIRMED', 'RECEIVED'];

  return (
    <Card data-testid="procurement-pos-card">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-slate-600" />
            <div>
              <CardTitle>Milk Run Orders with Delivery Details</CardTitle>
              <CardDescription>
                Generated POs with SOB% allocation, lead times, and milk run schedules. 
                Select POs to release in bulk.
              </CardDescription>
            </div>
          </div>
          <div className="flex gap-2">
            {selectedPOs.size > 0 && (
              <Badge variant="outline" className="text-blue-600">
                <CheckSquare className="w-3 h-3 mr-1" />
                {selectedPOs.size} Selected
              </Badge>
            )}
            {hasActiveFilters && (
              <Button variant="outline" size="sm" onClick={clearFilters} data-testid="clear-filters-btn">
                <X className="w-4 h-4 mr-2" />
                Clear Filters
              </Button>
            )}
            {/* Only show Release button if user has edit/create permission */}
            {hasPermission('procurement', 'edit') && (
              <Button 
                size="sm" 
                onClick={handleReleasePOs}
                disabled={selectedPOs.size === 0}
                className="bg-green-600 hover:bg-green-700 disabled:bg-gray-300"
              >
                <Send className="w-4 h-4 mr-2" />
                Release Selected
              </Button>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {/* Filter Section */}
        <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 mb-4">
          <div className="flex items-center gap-2 mb-3">
            <Filter className="w-4 h-4 text-slate-600" />
            <h3 className="font-semibold text-sm text-slate-700">Filters</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-3">
            <div>
              <label className="text-xs font-medium mb-1 block text-slate-600">MRO Number</label>
              <Input
                data-testid="filter-po-number"
                placeholder="Search PO..."
                value={filters.po_number}
                onChange={(e) => setFilters({ ...filters, po_number: e.target.value })}
                className="h-9"
              />
            </div>
            <div>
              <label className="text-xs font-medium mb-1 block text-slate-600">Vendor</label>
              <Select
                data-testid="filter-vendor"
                value={filters.vendor_id}
                onValueChange={(value) => setFilters({ ...filters, vendor_id: value })}
              >
                <SelectTrigger className="h-9">
                  <SelectValue placeholder="All vendors" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All vendors</SelectItem>
                  {vendors.map(v => (
                    <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-medium mb-1 block text-slate-600">Item</label>
              <SearchableItemSelect
                items={items}
                value={filters.item_id}
                onValueChange={(value) => setFilters({ ...filters, item_id: value })}
                placeholder="All items"
                dataTestId="filter-item"
              />
            </div>
            <div>
              <label className="text-xs font-medium mb-1 block text-slate-600">Status</label>
              <Select
                data-testid="filter-status"
                value={filters.status}
                onValueChange={(value) => setFilters({ ...filters, status: value })}
              >
                <SelectTrigger className="h-9">
                  <SelectValue placeholder="All statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  {uniqueStatuses.map(s => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
            <div>
              <label className="text-xs font-medium mb-1 block text-slate-600">Order Date</label>
              <Input
                data-testid="filter-order-date"
                type="date"
                value={filters.order_date}
                onChange={(e) => setFilters({ ...filters, order_date: e.target.value })}
                className="h-9"
              />
            </div>
            <div>
              <label className="text-xs font-medium mb-1 block text-slate-600">Need Date</label>
              <Input
                data-testid="filter-need-date"
                type="date"
                value={filters.need_date}
                onChange={(e) => setFilters({ ...filters, need_date: e.target.value })}
                className="h-9"
              />
            </div>
            <div>
              <label className="text-xs font-medium mb-1 block text-slate-600">SOB%</label>
              <Input
                data-testid="filter-sob"
                placeholder="e.g. 60%"
                value={filters.sob_tag}
                onChange={(e) => setFilters({ ...filters, sob_tag: e.target.value })}
                className="h-9"
              />
            </div>
            <div>
              <label className="text-xs font-medium mb-1 block text-slate-600">Qty Min</label>
              <Input
                data-testid="filter-qty-min"
                type="number"
                placeholder="Min"
                value={filters.qty_min}
                onChange={(e) => setFilters({ ...filters, qty_min: e.target.value })}
                className="h-9"
              />
            </div>
            <div>
              <label className="text-xs font-medium mb-1 block text-slate-600">Qty Max</label>
              <Input
                data-testid="filter-qty-max"
                type="number"
                placeholder="Max"
                value={filters.qty_max}
                onChange={(e) => setFilters({ ...filters, qty_max: e.target.value })}
                className="h-9"
              />
            </div>
          </div>
        </div>

        {/* Date Range Filter */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <span className="text-sm font-medium text-blue-700">Need Date Range:</span>
              <Input
                type="date"
                value={dateFilter.start_date}
                onChange={(e) => handleDateFilterChange('start_date', e.target.value)}
                className="h-9 w-40"
                data-testid="date-filter-start"
              />
              <span className="text-sm text-slate-500">to</span>
              <Input
                type="date"
                value={dateFilter.end_date}
                onChange={(e) => handleDateFilterChange('end_date', e.target.value)}
                className="h-9 w-40"
                data-testid="date-filter-end"
              />
              <Button size="sm" onClick={applyDateFilter} className="bg-blue-600 hover:bg-blue-700">
                Apply
              </Button>
              {(dateFilter.start_date || dateFilter.end_date) && (
                <Button size="sm" variant="outline" onClick={clearDateFilter}>
                  Clear
                </Button>
              )}
            </div>
            <div className="text-sm text-blue-700">
              Total: <strong>{pos.length}</strong> MROs
            </div>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-8 text-slate-500">Loading...</div>
        ) : filteredPos.length === 0 ? (
          <div data-testid="no-pos" className="text-center py-8 text-slate-500">
            {hasActiveFilters ? 'No milk run orders match the selected filters.' : 'No milk run orders. Generate MROs first.'}
          </div>
        ) : (
          <div>
            <div className="mb-2 text-sm text-slate-600">
              Showing <strong>{filteredPos.length}</strong> of <strong>{pos.length}</strong> records
            </div>
            <div className="overflow-auto max-h-[500px]">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12">
                      <input
                        type="checkbox"
                        checked={selectedPOs.size === filteredPos.length && filteredPos.length > 0}
                        onChange={handleSelectAll}
                        className="cursor-pointer"
                      />
                    </TableHead>
                    <TableHead>MRO Number</TableHead>
                    <TableHead>Vendor</TableHead>
                    <TableHead>Item</TableHead>
                    <TableHead>Order Date</TableHead>
                    <TableHead>Need Date</TableHead>
                    <TableHead className="text-right">Quantity</TableHead>
                    <TableHead>SOB%</TableHead>
                    <TableHead>
                      <div className="flex items-center gap-1">
                        <Truck className="w-4 h-4" />
                        Milk Run
                      </div>
                    </TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Activity</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredPos.map((po) => {
                    const vendorInfo = getVendorInfo(po.vendor_id);
                    const isSelected = selectedPOs.has(po.id);
                    const isPast = isPastDate(po.order_date);
                    const isSelectable = canSelectPO(po);
                    return (
                      <TableRow 
                        key={po.id} 
                        data-testid={`po-row-${po.id}`}
                        className={`${isSelected ? 'bg-blue-50' : ''} ${isPast ? 'bg-red-50 opacity-60' : ''}`}
                      >
                        {/* Only show checkbox if user has edit permission */}
                        {hasPermission('procurement', 'edit') && (
                          <TableCell>
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => handleSelectPO(po.id)}
                              disabled={!isSelectable}
                              className={`${isSelectable ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'}`}
                              title={isPast ? 'Cannot release past-dated POs' : (po.status !== 'DRAFT' ? 'Only DRAFT POs can be released' : '')}
                            />
                          </TableCell>
                        )}
                        <TableCell className="font-medium">
                          {po.po_number}
                          {isPast && (
                            <span className="ml-2 text-xs text-red-600" title="Past order date">🚫 Past Date</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Building className="w-4 h-4 text-slate-400" />
                            <div>
                              <div>{getVendorName(po.vendor_id)}</div>
                              <div className="text-xs text-slate-500">{vendorInfo.type}</div>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="text-sm">{getItemInfo(po.item_id)}</TableCell>
                        <TableCell>
                          <span className={isPast ? 'text-red-600 font-semibold' : ''}>
                            {po.order_date}
                          </span>
                        </TableCell>
                        <TableCell>{po.need_date}</TableCell>
                        <TableCell className="text-right">
                          {/* Only allow editing quantity if user is admin AND MRO is in draft state (DRAFT or URGENT_DRAFT) */}
                          {isAdmin() && (po.status === 'DRAFT' || po.status === 'URGENT_DRAFT') ? (
                            <Input
                              type="number"
                              value={Math.round(quantities[po.id] || 0)}
                              onChange={(e) => handleQuantityChange(po.id, e.target.value)}
                              className="w-24 h-8 text-right"
                              min="0"
                            />
                          ) : (
                            <span className="font-medium">{Math.round(quantities[po.id] || 0)}</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="bg-blue-50">{po.sob_tag}</Badge>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="bg-green-50 text-green-700">
                            <Truck className="w-3 h-3 mr-1" />
                            {vendorInfo.milk_run_freq || 'N/A'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge className={getStatusColor(po.status)}>{po.status}</Badge>
                          {po.shortfall_notification && (
                            <span className="block text-xs text-orange-600 mt-1" title={po.shortfall_notification}>
                              ⚠️ Shortfall
                            </span>
                          )}
                          {po.reallocation_note && (
                            <span className="block text-xs text-blue-600 mt-1" title={po.reallocation_note}>
                              🔄 Reallocated
                            </span>
                          )}
                        </TableCell>
                        <TableCell>
                          <ActivityLogButton recordId={po.id} recordLabel={po.po_number} />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ProcurementView;
